import overlayCSS from '../styles/overlay.css';
import overlayHTML from '../templates/overlay.tpl.html';
import verifyHTML from '../templates/verify.tpl.html';
import {createStyleNode} from './utils';
import {Template} from './Template';
import {INTERNAL_OPTIONS} from './options';

class OverlayRenderer {
    constructor(scope, contentBlurRenderer, accessManager) {
        this.scope = scope;
        this.contentBlurRenderer = contentBlurRenderer;
        this.accessManager = accessManager;
    }

    // Configure overlay renderer
    configure(options) {
        this.options = options;

        if (-1 === ['inline', 'redirect', 'auto-redirect'].indexOf(this.options.verifyMode)) {
            throw '[AGEGO] Invalid configuration - verifyMode invalid';
        }

        if (!this.options.requireAgeVerification && !this.options.allowDirectContinue) {
            throw '[AGEGO] Invalid configuration - verification and direct continue disabled';
        }
    }

    render(redirectError) {
        const listener = () => {
            this.scope.removeEventListener('DOMContentLoaded', listener);
            this.createAttachOverlay(redirectError);
        }
        this.scope.addEventListener('DOMContentLoaded', listener, false);
    }

    createAttachOverlay(redirectError) {
        this.style = createStyleNode(this.scope, overlayCSS);
        this.scope.document.head.appendChild(this.style);

        this.template = new Template(this.scope, overlayHTML, 'agego-overlay-container');
        this.prepareOverlayPrompt();
        this.template.render(this.scope.document.body);

        if (redirectError) {
            this.onVerifyFailure(redirectError);
        }
    }

    onDirectContinueAction() {
        if (!this.options.allowDirectContinue) {
            return;
        }

        try {
            this.options.events.onDirectContinue();
        } catch (e) {
            console.error('[AGEGO] error in event listener', e)
        }

        this.accessManager.onDirectContinue();
        this.destroy();
    }

    onVerifyAction() {
        if (!this.options.requireAgeVerification) {
            return;
        }

        try {
            this.options.events.onAgeVerify();
        } catch (e) {
            console.error('[AGEGO] error in event listener', e)
        }

        const nonce = this.accessManager.getCreateSessionNonce();

        // Redirect flow
        if ('redirect' === this.options.verifyMode) {
            this.scope.location.href = this.accessManager.createRedirectURL(INTERNAL_OPTIONS.endpoint, nonce);
            return;
        }

        // Inline flow
        const messageListener = (e) => {
            if (INTERNAL_OPTIONS.frontend !== e.origin) {
                throw '[AGEGO] message event origin fail '
            }

            const eventData = JSON.parse(e.data);

            this.scope.removeEventListener('message', messageListener);
            const result = this.accessManager.checkVerifiedByParams(eventData, nonce);

            if (true === result) {
                this.accessManager.prefersLongSession = !!(eventData['ag_remember_me'] &&
                    'yes' === eventData['ag_remember_me']);
                this.accessManager.onAgeVerified();
                this.destroy();
                try {
                    this.options.events.onVerificationFlowEnd(eventData)
                } catch (e) {
                    console.error('[AGEGO] error in event listener', e)
                }
                return;
            }

            this.onVerifyFailure(result);
        };

        this.scope.addEventListener('message', messageListener, false);

        this.template.replace(verifyHTML);
        this.template.container.className = 'verify';

        const loader = this.template.byId('agego-verify-frame-loading');
        const frame = this.template.byId('agego-verify-frame');

        let isLoaded = false;
        const onFrameLoaded = () => {
            isLoaded = true;
            loader.style.display = 'none';
            frame.style.display = 'block';
            frame.removeEventListener('load', onFrameLoaded);
        };

        setTimeout(() => {
            if (isLoaded) {
                return;
            }

            try {
                frame.stop();
            } catch (_) {
                // NOOP
            }

            this.onVerifyFailure({error: 'internalError'})
        }, INTERNAL_OPTIONS.iframeTimeout);

        frame.addEventListener('load', onFrameLoaded);
        frame.src = this.accessManager.createRedirectURL(INTERNAL_OPTIONS.endpoint, nonce);
    }

    destroy() {
        this.contentBlurRenderer.destroy();

        if (this.template) {
            this.template.destroy();
        }
    }

    onVerifyFailure(error) {
        console.debug('Verification failed', error);

        this.template.replace(overlayHTML);
        this.prepareOverlayPrompt();

        const errorPrompt = this.template.byId('agego-prompt-error');
        const errorPromptMessage = this.template.byId('agego-prompt-error-message');
        const message = this.options.overlay.messages.errors[error.error];

        errorPrompt.style.display = 'flex';
        errorPromptMessage.innerHTML = '';
        errorPromptMessage.appendChild(this.scope.document.createTextNode(message));

        try {
            this.options.events.onVerificationFlowFailed(error)
        } catch (e) {
            console.error('[AGEGO] error in event listener', e)
        }
    }

    prepareOverlayPrompt() {
        this.template.container.className = '';

        const ctaButtonVerify = this.template.byId('agego-cta-verify');
        const ctaButtonDirect = this.template.byId('agego-cta-direct');
        const ctaButtonUnderage = this.template.byId('agego-cta-underage');

        if (this.options.requireAgeVerification) {
            ctaButtonVerify.addEventListener('click', this.onVerifyAction.bind(this));
        } else {
            ctaButtonVerify.style.display = 'none';
            ctaButtonDirect.className = 'agego-cta agego-cta-style-primary';
        }

        if (this.options.allowDirectContinue) {
            ctaButtonDirect.addEventListener('click', this.onDirectContinueAction.bind(this));
        } else {
            ctaButtonDirect.style.display = 'none';
        }

        ctaButtonUnderage.addEventListener('click', () => {
            try {
                this.options.events.onUnderageSkip();
            } catch (e) {
                console.error('[AGEGO] error in event listener', e)
            }
            this.scope.location.href = this.options.underageRedirectTo;
        });

        this.applyStyling();
    }

    applyStyling() {
        const logoImage = this.template.byId('agego-overlay-content-logo-image');
        logoImage.src = this.options.overlay.logo;

        const overlay = this.template.byId('agego-overlay');

        overlay.style.background = this.options.overlay.style.modal.background;
        overlay.style.color = this.options.overlay.style.modal.text;

        const overlayHeader = this.template.byId('agego-overlay-header');
        overlayHeader.style.background = this.options.overlay.style.header.background;

        const overlayHeading = this.template.byId('agego-prompt-heading');
        overlayHeading.innerHTML = this.options.overlay.content.heading;

        const overlayDescription = this.template.byId('agego-prompt-about');
        overlayDescription.innerHTML = this.options.overlay.content.description;

        const ctaAgeVerify = this.template.byId('agego-cta-verify');
        ctaAgeVerify.style.background = this.options.overlay.style.ctaAgeVerify.background;
        ctaAgeVerify.style.color = this.options.overlay.style.ctaAgeVerify.text;

        const ctaAgeVerifyCta = this.template.byId('agego-cta-verify-cta');
        ctaAgeVerifyCta.innerHTML = this.options.overlay.content.ctaAgeVerify.cta;

        const ctaAgeVerifyLabel = this.template.byId('agego-cta-verify-label');
        ctaAgeVerifyLabel.innerHTML = this.options.overlay.content.ctaAgeVerify.label;

        const ctaDirectContinue = this.template.byId('agego-cta-direct');
        ctaDirectContinue.style.background = this.options.overlay.style.ctaDirectContinue.background;
        ctaDirectContinue.style.color = this.options.overlay.style.ctaDirectContinue.text;

        const ctaDirectContinueCta = this.template.byId('agego-cta-direct-cta');
        ctaDirectContinueCta.innerHTML = this.options.overlay.content.ctaDirectContinue.cta;

        const ctaDirectContinueLabel = this.template.byId('agego-cta-direct-label');
        ctaDirectContinueLabel.innerHTML = this.options.overlay.content.ctaDirectContinue.label;

        const ctaUnderageSkip = this.template.byId('agego-cta-underage');
        ctaUnderageSkip.style.color = this.options.overlay.style.ctaUnderageSkip.text;
        ctaUnderageSkip.innerHTML = this.options.overlay.content.ctaUnderageSkip.cta;
    }
}

export {OverlayRenderer};
